import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/api";
import { useSiteContent } from "@/hooks/useSiteContent";

const TEXT_FIELDS = [
  { key: "hero_subline", label: "Home — sottotitolo sotto il titolo principale", rows: 3 },
  { key: "intro_text", label: "Home — testo introduttivo (blocco Chi siamo)", rows: 5 },
  { key: "mission_quote", label: "Pagina Mission — citazione principale", rows: 4 },
  { key: "chi_siamo_p1", label: "Chi Siamo — paragrafo 1", rows: 3 },
  { key: "chi_siamo_p2", label: "Chi Siamo — paragrafo 2", rows: 3 },
  { key: "chi_siamo_p3", label: "Chi Siamo — paragrafo 3", rows: 4 },
  { key: "innovation_text", label: "Sezione Innovazione — testo", rows: 4 },
];

export default function AdminTextsForm() {
  const { content, isLoading } = useSiteContent();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isLoading && !form) {
      setForm(Object.fromEntries(TEXT_FIELDS.map((f) => [f.key, content[f.key] || ""])));
    }
  }, [isLoading, content, form]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (saving || !form) return;
    setSaving(true);
    try {
      await api.put("/admin/content", form);
      queryClient.invalidateQueries({ queryKey: ["site-content"] });
      toast.success("Testi aggiornati! Il sito è già aggiornato.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !form) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-7 w-7 animate-spin text-brand-cyan" />
      </div>
    );
  }

  const inputCls =
    "w-full border border-white/15 bg-brand-slate/60 px-4 py-3.5 text-sm text-white placeholder:text-white/35 outline-none transition focus:border-brand-ice focus:ring-1 focus:ring-brand-ice";

  return (
    <form onSubmit={save} data-testid="admin-texts-form" className="max-w-3xl space-y-6">
      {TEXT_FIELDS.map((f) => (
        <div key={f.key}>
          <label htmlFor={`tf-${f.key}`} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
            {f.label}
          </label>
          <textarea
            id={`tf-${f.key}`}
            rows={f.rows}
            value={form[f.key]}
            onChange={set(f.key)}
            className={`${inputCls} resize-y`}
            data-testid={`admin-text-field-${f.key}`}
          />
        </div>
      ))}
      <button
        type="submit"
        disabled={saving}
        data-testid="admin-save-texts-button"
        className="flex items-center gap-3 bg-brand-cyan px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-brand-teal transition hover:bg-brand-ice disabled:cursor-not-allowed disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Salva i testi
      </button>
    </form>
  );
}
